import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';

const Welcome = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Redirect to onboarding if user hasn't completed it
  useEffect(() => {
    if (currentUser && currentUser.onboardingComplete === false) {
      navigate('/onboarding');
    }
  }, [currentUser, navigate]);

  // Simplified check - adjust based on your actual data structure
  const hasDisciples = currentUser?.discipleIds?.length > 0 || currentUser?.disciples?.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-['Courgette'] italic text-[#0076BE] mb-3">
            Welcome to the TOM Community
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Students of Jesus on the journey of making disciples all over the world.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Video Section with Other Opportunities */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="aspect-w-16 aspect-h-9">
                <iframe
                  className="w-full h-full"
                  src="https://player.vimeo.com/video/YOUR_VIDEO_ID"
                  title="Welcome Video"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            {/* Other Opportunities Section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-[#0076BE] mb-3">
                Other opportunities:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#5ABA47] mr-2"></span>
                  Join your Church/Organization to work together
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#5ABA47] mr-2"></span>
                  Create a group of disciples
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#5ABA47] mr-2"></span>
                  Share a story on TOM
                </li>
              </ul>
            </div>
          </div>

          {/* Getting Started Steps */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-[#0076BE] mb-4">
              Getting Started
            </h2>
            
            {/* Step 1 */}
            <div className="mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#5ABA47] text-white font-semibold">
                    1
                  </span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {hasDisciples ? 'Add Another Disciple' : 'Add Your First Disciple'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Begin tracking your discipleship journey. Enter their name and basic information to create their profile.
                  </p>
                  <Link
                    to="/add-disciple"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#5ABA47] hover:bg-[#4a9d3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5ABA47]"
                  >
                    {hasDisciples ? 'Add Another Disciple' : 'Add Your First Disciple Now'}
                  </Link>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#5ABA47] text-white font-semibold">
                    2
                  </span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    What Happens Next
                  </h3>
                  <p className="text-gray-600 text-sm">
                    You'll receive helpful reminders to connect with your disciples at the frequency you choose. Track spiritual conversations, document growth, and stay organized with our reminder system.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#5ABA47] text-white font-semibold">
                    3
                  </span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    Stay Connected
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Get notifications when it's time to reach out to your disciples. Schedule meetings, share resources, and provide encouragement - all with timely reminders to help you stay consistent.
                  </p>
                </div>
              </div>
            </div>

            {/* Go to Dashboard Button (Conditional visibility or modified text could also work) */}
            <Link
              to="/dashboard"
              className="mt-6 w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#0076BE] hover:bg-[#005f98] transition-colors duration-200"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome; 